import { useState } from "react";
import { defaultLocation, inboundTemplate, lastPlace, pantryQtyText, pantryStockTable, parseInboundTable, planPantryTable, stockLines, uuidv7 } from "@lagerlog/core";
import type { Booking, PantryTablePlan, PantryTableRow } from "@lagerlog/core";
import { pricesShown } from "../catalog.ts";
import type { Catalog } from "../catalog.ts";
import { useLedger, useStore } from "../hooks.ts";
import { compareLagers } from "../brand.ts";
import { today } from "../download.ts";
import { isTableFile, readTable, writePantryBook } from "../pantryBook.ts";
import { Emoji } from "./Emoji.tsx";

/** The storages of this device, in the app's order. */
function deviceLagers(catalog: Catalog, scope: readonly string[]) {
  const cmp = compareLagers((id) => catalog.lagers.get(id)?.name ?? "");
  return [...catalog.lagers.values()].filter((l) => scope.includes(l.id)).sort((a, b) => cmp(a.id, b.id));
}

/**
 * The pantry as a table (docs/18-vorratsbuch.md, Bestandsliste), under the list of Vorräte for
 * owners and managers: the stock list as Excel, the template, and uploading a list again, which
 * changes master data and books opening stock only for articles never booked (pantry-table.ts).
 */
export function PantryTableCard({ catalog, onPlan }: { catalog: Catalog; onPlan: (plan: PantryTablePlan, file: string) => void }) {
  const { store } = useStore();
  const ledger = useLedger();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scope = store.config?.lagers ?? [];

  async function download() {
    if (!ledger) return;
    const rows = pantryStockTable([...catalog.articles.values()], ledger, deviceLagers(catalog, scope), pricesShown(catalog, store.prices));
    await writePantryBook(rows, "Bestand", `VorratsBuch-Bestand ${today()}.xlsx`);
  }

  async function upload(file: File) {
    if (!ledger) return;
    setError(null);
    if (!isTableFile(file)) return setError("Bitte eine Excel-Datei (.xlsx) oder CSV wählen.");
    setBusy(true);
    try {
      const { rows } = await readTable(file);
      const doc = parseInboundTable(rows, { stockList: true });
      // Articles with any booking keep their stock: the journal on this device and what is in stock.
      const booked = new Set<string>(stockLines(ledger).map((l) => l.article));
      for (const b of store.replica?.journal() ?? []) if ("article" in b && typeof b.article === "string") booked.add(b.article);
      const plan = planPantryTable(doc, [...catalog.articles.values()], ledger, booked, deviceLagers(catalog, scope), () => uuidv7(Date.now()));
      if (plan.rows.length === 0) return setError(plan.notes[0] ?? "In der Tabelle stehen keine Vorräte.");
      onPlan(plan, file.name);
    } catch (e) {
      console.error(e);
      setError("Die Tabelle ließ sich nicht lesen. Bitte als Excel-Datei (.xlsx) oder CSV speichern.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" data-testid="pantry-table">
      <strong>Tabelle (Excel)</strong>
      <p className="muted">Alle Vorräte mit Bestand als Excel-Datei, zum Ändern am Rechner. Dieselbe Datei lässt sich hier wieder hochladen: Geändert werden die Angaben, neue Vorräte werden angelegt. Den Bestand ändern Buchen und die Inventur. Was jede Spalte bedeutet, steht im Blatt „Erklärungen“.</p>
      <div className="table-actions">
        <button disabled={busy} onClick={() => void download()} data-testid="pantry-export">
          Als Excel herunterladen
        </button>
        <label className={`button file-pick ${busy ? "disabled" : ""}`} data-testid="pantry-upload-pick">
          {busy ? "Lese die Tabelle …" : "Tabelle hochladen"}
          <input
            type="file"
            accept=".xlsx,.csv"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void upload(file);
            }}
            data-testid="pantry-upload"
          />
        </label>
        <button className="link" onClick={() => void writePantryBook(inboundTemplate(), "Vorrat", "VorratsBuch-Vorlage.xlsx")} data-testid="pantry-template">
          Leere Vorlage herunterladen
        </button>
      </div>
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}

/** What an uploaded list changes, before it is taken over: new, changed, opening stock, notes. */
export function PantryTablePreview({ plan, file, catalog, onDone }: { plan: PantryTablePlan; file: string; catalog: Catalog; onDone: (message: string | null) => void }) {
  const { store } = useStore();
  const ledger = useLedger();
  const [busy, setBusy] = useState(false);
  const shown = plan.rows.filter((r) => r.state !== "same");
  const count = (s: PantryTableRow["state"]) => plan.rows.filter((r) => r.state === s).length;
  const openings = plan.rows.reduce((n, r) => n + r.opening.length, 0);
  const notes = [...plan.notes, ...plan.rows.flatMap((r) => r.notes.map((n) => `${r.name}: ${n}`))];
  const fieldsOf = (r: PantryTableRow) => (r.state === "new" ? r.fields : { ...(catalog.articles.get(r.article)?.fields ?? {}), ...r.fields });
  const what = (r: PantryTableRow) => {
    const start = r.opening.reduce((n, o) => n + o.qty, 0);
    // „neu“ stands on the right already; a new one without stock says it is created.
    const parts = [r.state === "new" ? "" : r.changed.join(", "), start ? `Startbestand ${pantryQtyText(start, fieldsOf(r))}` : ""].filter(Boolean);
    return parts.join(" · ") || "wird angelegt";
  };

  async function apply() {
    if (!ledger) return;
    setBusy(true);
    const scope = store.config?.lagers ?? [];
    const journal = store.replica?.journal() ?? [];
    let booked = 0;
    for (const r of plan.rows) {
      if (r.state === "same") continue;
      if (Object.keys(r.fields).length) await store.edit("article", r.article, r.fields);
      const fields = fieldsOf(r);
      for (const o of r.opening) {
        const places = [...catalog.locations.values()]
          .filter((l) => (o.lager ? l.lager === o.lager : scope.includes(l.lager)))
          .sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }))
          .map((l) => ({ id: l.id, lager: l.lager, stock: 0 }));
        const where = defaultLocation({ fields, places, lagerProfile: (id) => catalog.lagers.get(id)?.profile, lagerName: (id) => catalog.lagers.get(id)?.name ?? "", last: lastPlace(journal, r.article) });
        const location = catalog.locations.get(where);
        if (!location) continue;
        await store.book({
          kind: "in",
          article: r.article,
          lager: location.lager,
          location: location.id,
          qty: o.qty,
          reason: "opening",
          at: Date.now(),
          ...(o.amount !== undefined ? { amount: o.amount } : {}),
          ...(o.bestBefore ? { lotInfo: { bestBefore: o.bestBefore } } : {}),
        } as Booking);
        booked++;
      }
    }
    setBusy(false);
    const parts = [count("new") ? `${count("new")} neu angelegt` : "", plan.rows.filter((r) => r.state === "changed" && r.changed.length).length ? `${plan.rows.filter((r) => r.state === "changed" && r.changed.length).length} geändert` : "", booked ? `${booked} Startbestände gebucht` : ""].filter(Boolean);
    onDone(parts.length ? `Übernommen: ${parts.join(", ")}.` : "Nichts zu ändern.");
  }

  return (
    <div className="pantry-table-preview" data-testid="pantry-table-preview">
      <section className="card">
        <strong>Tabelle · {file}</strong>
        <p data-testid="pantry-table-summary">
          {count("new")} neu · {count("changed")} geändert · {count("same")} unverändert
          {openings ? ` · ${openings} ${openings === 1 ? "Startbestand" : "Startbestände"}` : ""}
        </p>
        {shown.length === 0 ? (
          <p className="muted">Die Tabelle ändert nichts.</p>
        ) : (
          <ul className="list" data-testid="pantry-table-rows">
            {shown.map((r) => (
              <li key={r.article} className="row between">
                <span className="named-emoji">
                  <Emoji fields={fieldsOf(r)} />
                  <span className="text">
                    {r.name}
                    <br />
                    <span className="muted">{what(r)}</span>
                  </span>
                </span>
                <span className="side boxed">
                  <span className="stack">
                    <span>{r.state === "new" ? "neu" : "geändert"}</span>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {notes.length > 0 && (
        <section className="card" data-testid="pantry-table-notes">
          <strong>Hinweise</strong>
          <ul>
            {notes.map((n, i) => (
              <li key={i} className="muted">
                {n}
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="table-actions">
        <button className="primary" disabled={busy || shown.length === 0} onClick={() => void apply()} data-testid="pantry-table-apply">
          {busy ? "Übernehme …" : "Übernehmen"}
        </button>
        <button disabled={busy} onClick={() => onDone(null)} data-testid="pantry-table-cancel">
          Abbrechen
        </button>
      </div>
    </div>
  );
}
